// src/services/newsletterAdminService.ts
// Admin-only client for the newsletter broadcast endpoints mounted at
// /api/admin/newsletters. All calls rely on apiService's axios interceptor
// to inject the Supabase JWT; the backend enforces admin role.

import { apiService } from "./apiService";

export type NewsletterCadence = "weekly" | "biweekly" | "monthly" | "custom";
export type NewsletterStatus = "active" | "paused";
export type EditionStatus = "draft" | "approved" | "sending" | "sent" | "failed";
export type TopicStatus = "pending" | "used" | "skipped";

export interface Newsletter {
  id: string;
  name: string;
  description: string | null;
  status: NewsletterStatus;
  cadence: NewsletterCadence;
  custom_cadence_days: number | null;
  send_day_of_week: number | null;
  send_hour_utc: number;
  llm_system_prompt: string;
  runtime_system_prompt: string | null;
  topic_batch_size: number;
  topic_refill_threshold: number;
  topics_last_planned_at: string | null;
  subject_template: string;
  next_run_at: string | null;
  last_sent_at: string | null;
  created_at: string;
  updated_at: string;
  created_by: string;
}

export interface NewsletterWithMeta extends Newsletter {
  edition_count: number;
  latest_edition_status?: EditionStatus;
  pending_topic_count: number;
  used_topic_count: number;
}

export interface NewsletterEdition {
  id: string;
  newsletter_id: string;
  edition_number: number;
  topic_id: string | null;
  status: EditionStatus;
  notes: string | null;
  subject: string | null;
  generated_html: string | null;
  generation_model: string | null;
  generation_error: string | null;
  generated_at: string | null;
  approved_at: string | null;
  approved_by: string | null;
  send_session_id: string | null;
  sent_at: string | null;
  recipient_count: number | null;
  created_at: string;
  updated_at: string;
}

export interface NewsletterTopic {
  id: string;
  newsletter_id: string;
  position: number;
  title: string;
  description: string | null;
  status: TopicStatus;
  used_by_edition_id: string | null;
  used_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateNewsletterInput {
  name: string;
  description?: string;
  cadence: NewsletterCadence;
  custom_cadence_days?: number;
  send_day_of_week?: number;
  send_hour_utc?: number;
  llm_system_prompt: string;
  subject_template: string;
  topic_batch_size?: number;
  topic_refill_threshold?: number;
}

export type UpdateNewsletterInput = Partial<CreateNewsletterInput>;

export interface PreviewInput {
  llm_system_prompt: string;
  subject_template: string;
  notes?: string;
}

export interface PreviewResult {
  subject: string;
  /** LLM-generated body HTML (unwrapped). Use this when persisting/editing. */
  html: string;
  /** Full email with brand header + dark theme. Use this for previews to show
   *  what the recipient will actually see. */
  wrappedHtml: string;
}

interface ApiEnvelope<T> {
  success: boolean;
  data: T;
}

const BASE = "/api/admin/newsletters";

const unwrap = <T,>(res: { data: ApiEnvelope<T> }): T => res.data.data;

export const newsletterAdminService = {
  list: async (): Promise<Newsletter[]> =>
    unwrap(await apiService.get(BASE)),

  get: async (id: string): Promise<NewsletterWithMeta> =>
    unwrap(await apiService.get(`${BASE}/${id}`)),

  create: async (input: CreateNewsletterInput): Promise<Newsletter> =>
    unwrap(await apiService.post(BASE, input)),

  preview: async (input: PreviewInput): Promise<PreviewResult> =>
    unwrap(await apiService.post(`${BASE}/preview`, input)),

  /** Wrap a (subject, body) pair in the branded email template. Used by the
   *  edition editor to show a live branded preview as the admin types. */
  renderHtml: async (subject: string, html: string): Promise<string> => {
    const res = await apiService.post(`${BASE}/render`, { subject, html });
    return unwrap<{ wrappedHtml: string }>(res).wrappedHtml;
  },

  sendPreviewTest: async (
    subject: string,
    html: string,
    to?: string
  ): Promise<{ success: boolean; messageId?: string; provider: string; error?: string }> =>
    unwrap(
      await apiService.post(`${BASE}/preview/send-test`, {
        subject,
        html,
        ...(to ? { to } : {}),
      })
    ),

  update: async (id: string, input: UpdateNewsletterInput): Promise<Newsletter> =>
    unwrap(await apiService.put(`${BASE}/${id}`, input)),

  remove: async (id: string): Promise<void> => {
    await apiService.delete(`${BASE}/${id}`);
  },

  setStatus: async (id: string, status: NewsletterStatus): Promise<Newsletter> =>
    unwrap(await apiService.patch(`${BASE}/${id}/status`, { status })),

  listEditions: async (id: string): Promise<NewsletterEdition[]> =>
    unwrap(await apiService.get(`${BASE}/${id}/editions`)),

  getEdition: async (id: string, eid: string): Promise<NewsletterEdition> =>
    unwrap(await apiService.get(`${BASE}/${id}/editions/${eid}`)),

  createEdition: async (id: string, notes?: string): Promise<NewsletterEdition> =>
    unwrap(await apiService.post(`${BASE}/${id}/editions`, { notes })),

  updateEdition: async (
    id: string,
    eid: string,
    subject: string,
    generated_html: string
  ): Promise<NewsletterEdition> =>
    unwrap(
      await apiService.put(`${BASE}/${id}/editions/${eid}`, { subject, generated_html })
    ),

  regenerateEdition: async (
    id: string,
    eid: string,
    notes?: string
  ): Promise<NewsletterEdition> =>
    unwrap(
      await apiService.post(`${BASE}/${id}/editions/${eid}/regenerate`, { notes })
    ),

  approveEdition: async (id: string, eid: string): Promise<NewsletterEdition> =>
    unwrap(await apiService.post(`${BASE}/${id}/editions/${eid}/approve`)),

  sendEdition: async (
    id: string,
    eid: string
  ): Promise<{
    sendSessionId: string | null;
    totalContacts: number;
    successCount: number;
    failureCount: number;
  }> => unwrap(await apiService.post(`${BASE}/${id}/editions/${eid}/send`)),

  resendFailedEdition: async (
    id: string,
    eid: string
  ): Promise<{
    retried: number;
    converted: number;
    recipientCount: number;
  }> =>
    unwrap(
      await apiService.post(`${BASE}/${id}/editions/${eid}/resend-failed`)
    ),

  sendTestEdition: async (
    id: string,
    eid: string,
    to?: string
  ): Promise<{ success: boolean; messageId?: string; provider: string; error?: string }> =>
    unwrap(
      await apiService.post(
        `${BASE}/${id}/editions/${eid}/send-test`,
        to ? { to } : {}
      )
    ),

  // --- Topic plan / queue ---

  listTopics: async (id: string): Promise<NewsletterTopic[]> =>
    unwrap(await apiService.get(`${BASE}/${id}/topics`)),

  createTopic: async (
    id: string,
    input: { title: string; description?: string; position?: number }
  ): Promise<NewsletterTopic> =>
    unwrap(await apiService.post(`${BASE}/${id}/topics`, input)),

  updateTopic: async (
    id: string,
    tid: string,
    input: { title?: string; description?: string | null }
  ): Promise<NewsletterTopic> =>
    unwrap(await apiService.put(`${BASE}/${id}/topics/${tid}`, input)),

  deleteTopic: async (id: string, tid: string): Promise<void> => {
    await apiService.delete(`${BASE}/${id}/topics/${tid}`);
  },

  reorderTopics: async (
    id: string,
    orderedIds: string[]
  ): Promise<NewsletterTopic[]> =>
    unwrap(
      await apiService.post(`${BASE}/${id}/topics/reorder`, { orderedIds })
    ),

  refillTopics: async (
    id: string,
    count?: number
  ): Promise<{ added: number; skipped: number }> =>
    unwrap(
      await apiService.post(
        `${BASE}/${id}/topics/refill`,
        count ? { count } : {}
      )
    ),

  regenerateTopicPlan: async (
    id: string
  ): Promise<{ runtime_system_prompt: string; topics: Array<{ title: string; description: string }> }> =>
    unwrap(await apiService.post(`${BASE}/${id}/topic-plan/regenerate`)),

  updateRuntimePrompt: async (
    id: string,
    runtime_system_prompt: string
  ): Promise<Newsletter> =>
    unwrap(
      await apiService.put(`${BASE}/${id}/runtime-prompt`, {
        runtime_system_prompt,
      })
    ),

  /**
   * Realistic dry-run: pick the next pending topic, run the same generation
   * the cron would run, and email the result to the admin (or `to`). The
   * topic is NOT consumed and no edition row is written.
   */
  sendRealisticTest: async (
    id: string,
    to?: string
  ): Promise<{
    success: boolean;
    messageId?: string;
    provider: string;
    error?: string;
    topicTitle: string;
    subject: string;
  }> =>
    unwrap(
      await apiService.post(
        `${BASE}/${id}/test-broadcast`,
        to ? { to } : {}
      )
    ),
};

export default newsletterAdminService;
